// Custom reviews UI for the product page. Judge.me is still the system of record,
// but the storefront never talks to it directly — everything comes from the Tommy
// backend: POST /graphql for the rating summary, /reviews/:productId for the list,
// eligibility and submission.
//
// Markup lives in snippets/product-reviews-drawer.liquid; this file only fills in
// text, toggles state classes and clones the Liquid-rendered <template> nodes.

// Asset files are classic scripts sharing one global scope, so every top-level
// name here is prefixed — content-drawer.js already owns a bare `FOCUSABLE`.
const REVIEWS_FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const REVIEWS_DRAWER_DURATION = 800; // keep in sync with --drawer-duration in base.css
const REVIEWS_EMPTY_HISTOGRAM = [0, 0, 0, 0, 0];

// Eligibility reason -> the data-* key holding its copy. A reason absent from this map
// shows nothing at all, which is now reserved for reasons the backend may add later:
// every reason the API documents today has copy.
const REVIEWS_NOTICE_KEYS = {
  already_reviewed: "msgAlreadyReviewed",
  not_delivered: "msgNotDelivered",
  not_purchased: "msgNotPurchased",
};

class ProductReviews extends HTMLElement {
  connectedCallback() {
    const {
      endpoint,
      brandSlug,
      productId,
      perPage,
      customerId,
      customerEmail,
      customerName,
      countTemplate,
      rangeTemplate,
      staticCount,
      staticAverage,
      staticHistogram,
    } = this.dataset;

    if (!endpoint || !productId) return;

    this.endpoint = endpoint.replace(/\/$/, "");
    this.brandSlug = brandSlug ?? "";
    this.productId = productId;
    this.perPage = parseInt(perPage, 10) || 5;
    this.customerId = customerId ?? "";
    this.customerEmail = customerEmail ?? "";
    this.customerName = customerName ?? "";
    this.countTemplate = countTemplate ?? "";
    this.rangeTemplate = rangeTemplate ?? "";
    this.staticCount = staticCount ? Number(staticCount) : null;
    this.staticAverage = staticAverage ? Number(staticAverage) : null;
    this.hasStaticHistogram = staticHistogram === "true";

    this.overlay = this.querySelector("[data-reviews-overlay]");
    this.panel = this.querySelector("[data-reviews-panel]");
    this.scrollArea = this.querySelector("[data-reviews-scroll]");
    this.closeButton = this.querySelector("[data-reviews-close]");
    this.listView = this.querySelector('[data-reviews-view="list"]');
    this.formView = this.querySelector('[data-reviews-view="form"]');

    this.heading = this.querySelector("[data-reviews-heading]");
    this.summaryEl = this.querySelector("[data-reviews-summary]");
    this.averageWrap = this.querySelector("[data-reviews-average-wrap]");
    this.average = this.querySelector("[data-reviews-average]");
    this.summaryStars = this.querySelector("[data-reviews-stars]");
    this.emptyNote = this.querySelector("[data-reviews-empty-note]");
    this.histogram = this.querySelector("[data-reviews-histogram]");
    this.detailsWrap = this.querySelector("[data-reviews-details-wrap]");
    this.detailsToggle = this.querySelector("[data-reviews-details-toggle]");
    this.detailsLabel = this.querySelector("[data-reviews-details-label]");
    this.detailsIcon = this.querySelector("[data-reviews-details-icon]");

    this.range = this.querySelector("[data-reviews-range]");
    this.list = this.querySelector("[data-reviews-list]");
    this.loadingNote = this.querySelector("[data-reviews-loading]");
    this.emptyState = this.querySelector("[data-reviews-empty]");
    this.errorNote = this.querySelector("[data-reviews-error]");
    this.moreButton = this.querySelector("[data-reviews-more]");

    this.loginLink = this.querySelector("[data-reviews-login]");
    this.writeButton = this.querySelector("[data-reviews-write]");
    this.noticeEl = this.querySelector("[data-reviews-notice]");
    this.backButton = this.querySelector("[data-reviews-back]");

    this.form = this.querySelector("[data-reviews-form]");
    this.formTitle = this.querySelector("[data-reviews-form-title]");
    this.formBody = this.querySelector("[data-reviews-form-body]");
    this.formError = this.querySelector("[data-reviews-form-error]");
    this.formSuccess = this.querySelector("[data-reviews-form-success]");
    this.formStatus = this.querySelector("[data-reviews-form-status]");
    this.formSubmit = this.querySelector("[data-reviews-form-submit]");
    this.ratingStars = [...this.querySelectorAll("[data-reviews-rating-star]")];

    this.photosRoot = this.querySelector("[data-reviews-photos]");
    this.photosInput = this.querySelector("[data-reviews-photos-input]");
    this.photosList = this.querySelector("[data-reviews-photos-list]");
    this.photoTemplate = this.querySelector(
      "template[data-review-photo-template]",
    );

    this.reviewTemplate = this.querySelector("template[data-review-template]");
    this.imageTemplate = this.querySelector(
      "template[data-review-image-template]",
    );

    this.isOpen = false;
    this.lastFocused = null;
    this.page = 0;
    this.totalCount = 0;
    this.loadedCount = 0;
    this.summaryCount = 0;
    this.hasMore = false;
    this.isLoading = false;
    this.hasLoadedList = false;
    this.hasCheckedEligibility = false;
    this.formRating = 0;
    this.photos = [];
    this.photoPreviewUrls = [];

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.addEventListener("click", (event) => {
      if (
        event.target.closest("[data-reviews-close]") ||
        event.target === this.overlay
      ) {
        this.close();
      }
    });

    this.detailsToggle?.addEventListener("click", () => this.toggleDetails());
    this.moreButton?.addEventListener("click", () => this.loadNextPage());
    this.writeButton?.addEventListener("click", () => this.showForm());
    this.backButton?.addEventListener("click", () => this.showList());
    this.form?.addEventListener("submit", (event) => this.submitReview(event));
    this.photosInput?.addEventListener("change", () => this.onPhotosChange());

    for (const star of this.ratingStars) {
      star.addEventListener("click", () => {
        this.formRating = parseInt(star.dataset.reviewsRatingStar, 10) || 0;
        this.renderFormRating();
      });
    }

    document.addEventListener("click", this.onTriggerClick);
    document.addEventListener("keydown", this.onKeydown);

    // Paint the metafield rating straight away so the drawer heading matches the
    // server-rendered trigger row even before the summary request resolves.
    if (this.staticCount !== null) {
      this.applySummary(this.staticCount, this.staticAverage ?? 0, null, 0);
    }

    // The trigger row lives outside this element (it is a product section block),
    // so the summary is fetched on load rather than on open.
    this.loadSummary();
  }

  disconnectedCallback() {
    document.removeEventListener("click", this.onTriggerClick);
    document.removeEventListener("keydown", this.onKeydown);
    this.releasePhotoPreviews();
  }

  onTriggerClick(event) {
    const trigger = event.target.closest("[data-reviews-trigger]");
    if (!trigger) return;
    event.preventDefault();
    this.lastFocused = trigger;
    this.open();
  }

  onKeydown(event) {
    if (!this.isOpen) return;
    if (event.key === "Escape") {
      this.close();
    } else if (event.key === "Tab") {
      this.trapFocus(event);
    }
  }

  open() {
    this.isOpen = true;
    this.classList.remove("hidden");
    this.setAttribute("aria-hidden", "false");
    document.body.classList.add("overflow-hidden");

    // Double rAF so the off-screen state paints before the slide-in transition.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.classList.add("is-open"));
    });

    // preventScroll: the panel is still parked off-viewport at this point, so a
    // plain focus() makes the browser scroll the page behind the drawer.
    this.closeButton?.focus({ preventScroll: true });

    if (!this.hasLoadedList) this.loadPage(1);
    if (!this.hasCheckedEligibility) this.checkEligibility();
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.classList.remove("is-open");
    this.setAttribute("aria-hidden", "true");

    setTimeout(() => {
      this.classList.add("hidden");
      document.body.classList.remove("overflow-hidden");
    }, REVIEWS_DRAWER_DURATION);

    this.lastFocused?.focus({ preventScroll: true });
  }

  trapFocus(event) {
    const focusable = [
      ...this.panel.querySelectorAll(REVIEWS_FOCUSABLE),
    ].filter((element) => element.offsetParent !== null);
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  async request(path, options = {}) {
    const response = await AuthToken.fetch(`${this.endpoint}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        "X-Brand-Slug": this.brandSlug,
        ...options.headers,
      },
    });

    const payload = await response.json().catch(() => null);

    // Failures come back as { success:false, error:{ code, message } }. The message is
    // nested (not top level) and the backend's copy is already shopper-ready, so it is
    // surfaced as-is; `code` is carried on the error so callers can react to specifics.
    // `success === false` is checked too, in case a failure ever arrives with a 2xx.
    if (!response.ok || payload?.success === false) {
      const failure = new Error(
        payload?.error?.message ?? `HTTP ${response.status}`,
      );
      failure.code = payload?.error?.code ?? null;
      throw failure;
    }
    // The backend wraps responses as { success, error, data } while its docs show
    // the bare object — accept either so a contract change does not break the UI.
    return payload?.data ?? payload;
  }

  async loadSummary() {
    const query =
      "query GetProduct($id: String!) { product(id: $id) { id ratingSummary { average count histogram } } }";
    const variables = { id: `gid://shopify/Product/${this.productId}` };

    try {
      const data = await this.request("/graphql", {
        method: "POST",
        body: JSON.stringify({ query, variables }),
      });
      this.renderSummary(data?.product?.ratingSummary ?? null);
    } catch (error) {
      // A failed summary must not hide the drawer — the review list still works.
      console.error("[ProductReviews] summary failed", error);
    }
  }

  // Shopify's rating metafields win over the API summary when present: they are the
  // value already painted into the trigger row, and the API summary is derived from a
  // Judge.me metafield that can lag behind published reviews.
  renderSummary(summary) {
    const apiCount = Number(summary?.count ?? 0);
    const count = this.staticCount ?? apiCount;
    const average = this.staticAverage ?? Number(summary?.average ?? 0);

    // No breakdown exists in any metafield, so the bars are only meaningful when the
    // API itself has reviews to describe.
    const histogram =
      apiCount > 0 ? (summary?.histogram ?? REVIEWS_EMPTY_HISTOGRAM) : null;

    this.applySummary(count, average, histogram, apiCount);
  }

  applySummary(count, average, histogram, histogramTotal) {
    this.summaryCount = count;
    this.summaryEl?.classList.toggle("is-empty", count === 0);

    if (count > 0 && this.heading && this.countTemplate) {
      this.heading.textContent = this.countTemplate.replace(
        "__TOTAL__",
        count.toString(),
      );
    }

    if (this.average) this.average.textContent = average.toFixed(1);
    this.averageWrap?.toggleAttribute("hidden", count === 0);
    this.emptyNote?.toggleAttribute("hidden", count > 0);

    // Bars drawn server-side from the metafield are already correct — the API summary is
    // the laggier source, so it must not overwrite them.
    const showHistogram = this.hasStaticHistogram || histogram !== null;
    this.detailsWrap?.toggleAttribute("hidden", !showHistogram);

    this.setStars(this.summaryStars, average);
    if (!this.hasStaticHistogram && histogram !== null) {
      this.renderHistogram(histogram, histogramTotal);
    }
    this.renderTriggerRow(count, average);
  }

  renderHistogram(histogram, count) {
    for (let star = 1; star <= 5; star += 1) {
      const row = this.querySelector(`[data-reviews-histogram-row="${star}"]`);
      if (!row) continue;

      // The API sends the histogram ascending, so index 0 is the 1-star bucket.
      const frequency = histogram[star - 1] ?? 0;
      const percent = count > 0 ? Math.round((frequency / count) * 100) : 0;

      const fill = row.querySelector("[data-reviews-bar-fill]");
      const value = row.querySelector("[data-reviews-bar-value]");
      // A custom property is the only way to express an arbitrary percentage —
      // the bar's styling itself stays in base.css.
      fill?.style.setProperty("--product-reviews-bar", `${percent}%`);
      if (value) value.textContent = `${percent}%`;
    }
  }

  renderTriggerRow(count, average) {
    const triggers = document.querySelectorAll("[data-reviews-trigger]");

    for (const trigger of triggers) {
      // Already rendered from the rating metafields — never downgrade it.
      if (trigger.hasAttribute("data-reviews-static")) continue;

      const countWrap = trigger.querySelector("[data-reviews-count-wrap]");
      const countTarget = trigger.querySelector("[data-reviews-count]");
      const starsWrap = trigger.querySelector("[data-reviews-stars-wrap]");
      const averageWrap = trigger.querySelector("[data-reviews-average-wrap]");
      const averageTarget = trigger.querySelector("[data-reviews-average]");

      if (countTarget) countTarget.textContent = count.toString();
      if (averageTarget) averageTarget.textContent = average.toFixed(1);

      countWrap?.toggleAttribute("hidden", count === 0);
      starsWrap?.toggleAttribute("hidden", count === 0);
      averageWrap?.toggleAttribute("hidden", count === 0);

      this.setStars(trigger.querySelector("[data-reviews-stars]"), average);
    }
  }

  setStars(container, rating) {
    if (!container) return;
    const stars = container.querySelectorAll(".icon-star");
    const filled = Math.round(rating);

    stars.forEach((star, index) => {
      star.classList.toggle("is-filled", index < filled);
    });
  }

  toggleDetails() {
    const isHidden = this.histogram?.hasAttribute("hidden") ?? true;

    this.histogram?.toggleAttribute("hidden", !isHidden);
    this.detailsToggle?.setAttribute("aria-expanded", isHidden.toString());
    this.detailsIcon?.classList.toggle("rotate-180", isHidden);

    if (this.detailsLabel) {
      const { labelShow, labelHide } = this.detailsToggle.dataset;
      this.detailsLabel.textContent = isHidden ? labelHide : labelShow;
    }
  }

  loadNextPage() {
    this.loadPage(this.page + 1);
  }

  async loadPage(page) {
    if (this.isLoading) return;
    this.isLoading = true;

    this.errorNote?.setAttribute("hidden", "");
    this.emptyState?.setAttribute("hidden", "");
    this.moreButton?.setAttribute("hidden", "");
    this.loadingNote?.removeAttribute("hidden");

    // No `sort`: the backend ignores it (identical order for every value), and the
    // drawer no longer offers the choice. See the note in product-reviews-drawer.
    const params = new URLSearchParams({
      page: page.toString(),
      perPage: this.perPage.toString(),
    });

    try {
      const data = await this.request(
        `/reviews/${this.productId}?${params.toString()}`,
      );

      const reviews = data?.reviews ?? [];
      const pagination = data?.pagination ?? {};

      this.page = pagination.page ?? page;
      this.hasMore = pagination.hasMore ?? false;

      // `totalCount` is per-page on later pages rather than the overall total (page 2 of
      // a 6-review product reports 1), so it can shrink below what is already rendered.
      // The metafield count is authoritative when present; otherwise keep the largest
      // figure seen, and renderRange floors it by what is actually on screen.
      const reportedTotal = pagination.totalCount ?? reviews.length;
      this.totalCount =
        this.staticCount ?? Math.max(this.totalCount, reportedTotal);

      // Only mark the list as loaded on success, so a failed open retries next time.
      this.hasLoadedList = true;

      this.renderReviews(reviews);
      this.renderRange();
      this.reconcileSummary();

      this.moreButton?.toggleAttribute("hidden", !this.hasMore);

      // The summary already says "be the first to review this item", so only fall back
      // to a list-level message when that note is not on screen.
      const summaryNoteShown =
        this.emptyNote != null && !this.emptyNote.hasAttribute("hidden");
      this.emptyState?.toggleAttribute(
        "hidden",
        this.loadedCount > 0 || summaryNoteShown,
      );
    } catch (error) {
      console.error("[ProductReviews] list failed", error);
      if (this.errorNote) {
        this.errorNote.textContent =
          this.apiMessage(error) ?? this.errorNote.dataset.fallback;
        this.errorNote.removeAttribute("hidden");
      }
    } finally {
      this.isLoading = false;
      this.loadingNote?.setAttribute("hidden", "");
    }
  }

  // The rating summary is derived from a Judge.me metafield that can lag days behind
  // published reviews, so the list is the more trustworthy count. Never let the header
  // claim there are no ratings while reviews are visible underneath it. The average is
  // deliberately left hidden — one page of reviews cannot produce a true one.
  reconcileSummary() {
    if (this.summaryCount > 0 || this.totalCount === 0) return;

    if (this.heading && this.countTemplate) {
      this.heading.textContent = this.countTemplate.replace(
        "__TOTAL__",
        this.totalCount.toString(),
      );
    }

    this.emptyNote?.setAttribute("hidden", "");
    this.summaryEl?.classList.remove("is-empty");
  }

  renderReviews(reviews) {
    if (!this.list || !this.reviewTemplate) return;

    const fragment = document.createDocumentFragment();

    for (const review of reviews) {
      const node = this.reviewTemplate.content.cloneNode(true);

      this.setStars(node.querySelector("[data-review-stars]"), review.rating);

      const name = node.querySelector("[data-review-name]");
      if (name) name.textContent = review.reviewerName ?? "";

      const date = node.querySelector("[data-review-date]");
      if (date) date.textContent = this.formatDate(review.createdAt);

      node
        .querySelector("[data-review-verified]")
        ?.toggleAttribute("hidden", review.verifiedPurchase !== true);

      const titleWrap = node.querySelector("[data-review-title-wrap]");
      const title = node.querySelector("[data-review-title]");
      const hasTitle = (review.title ?? "").trim() !== "";
      if (title && hasTitle) title.textContent = review.title;
      titleWrap?.toggleAttribute("hidden", !hasTitle);

      const body = node.querySelector("[data-review-body]");
      if (body) body.textContent = review.body ?? "";

      this.renderReviewImages(node, review.images);

      fragment.appendChild(node);
    }

    this.list.appendChild(fragment);
    this.loadedCount += reviews.length;
  }

  renderReviewImages(node, images) {
    const wrap = node.querySelector("[data-review-images-wrap]");
    const container = node.querySelector("[data-review-images]");
    const urls = (images ?? [])
      .map((image) => this.imageUrl(image))
      .filter(Boolean);

    if (!wrap || !container || urls.length === 0 || !this.imageTemplate) {
      wrap?.toggleAttribute("hidden", true);
      return;
    }

    for (const url of urls) {
      const imageNode = this.imageTemplate.content.cloneNode(true);
      const image = imageNode.querySelector("img");
      if (image) image.src = url;
      container.appendChild(imageNode);
    }

    wrap.removeAttribute("hidden");
  }

  // One review photo's URL, for the thumbnail grid.
  //
  // The API returns `images` as an array of { url, thumbnailUrl } — verified live against
  // GET /reviews/10556811378966 on 2026-08-31, where 5 of 20 reviews carried photos. An
  // earlier note here claimed it "never returns a populated one"; that was wrong, and it
  // sent an images bug to the backend that was really the `sort` param being ignored (the
  // reviews with photos were the last page, so page 1 looked empty).
  //
  // thumbnailUrl first, deliberately: it is ?width=160, while `url` is ?width=1024 and the
  // grid renders these at 96px (see template[data-review-image-template]) — about 40x the
  // pixel area for nothing, up to 5 times per review. Nothing opens them full-size, so the
  // large one is never wanted here. A bare string and the other shapes stay accepted in
  // case the payload changes.
  imageUrl(image) {
    if (typeof image === "string") return image;
    return image?.thumbnailUrl ?? image?.url ?? image?.src ?? "";
  }

  formatDate(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  renderRange() {
    if (!this.range || !this.rangeTemplate) return;
    if (this.loadedCount === 0) {
      this.range.textContent = "";
      return;
    }

    const total = Math.max(this.totalCount, this.loadedCount);

    this.range.textContent = this.rangeTemplate
      .replace("__FROM__", "1")
      .replace("__TO__", this.loadedCount.toString())
      .replace("__TOTAL__", total.toString());
  }

  async checkEligibility() {
    this.hasCheckedEligibility = true;

    if (!this.customerId) {
      this.loginLink?.removeAttribute("hidden");
      return;
    }

    try {
      const data = await this.request(
        `/reviews/${this.productId}/eligibility`,
        {
          method: "POST",
          body: JSON.stringify({ customerId: this.customerId }),
        },
      );

      if (data?.canReview === true) {
        this.writeButton?.removeAttribute("hidden");
        return;
      }

      // Say why the CTA is missing. Every reason the API returns today has copy, including
      // `not_purchased`, which is the one seen most — it covers every signed-in shopper on
      // every product they have not bought.
      //
      // An unrecognised reason still shows nothing. That keeps a reason the backend adds
      // later from rendering a blank notice, and is the same state a failed check leaves
      // behind (see the catch below).
      const key = REVIEWS_NOTICE_KEYS[data?.reason];
      if (key) this.showNotice(this.noticeEl?.dataset[key]);
    } catch (error) {
      // Eligibility is a gate, not content — stay silent and show nothing.
      console.error("[ProductReviews] eligibility failed", error);
    }
  }

  showForm() {
    this.listView?.setAttribute("hidden", "");
    this.formView?.removeAttribute("hidden");
    this.scrollArea?.scrollTo({ top: 0 });
    this.formTitle?.focus();
  }

  showList() {
    this.formView?.setAttribute("hidden", "");
    this.listView?.removeAttribute("hidden");
  }

  renderFormRating() {
    for (const star of this.ratingStars) {
      const value = parseInt(star.dataset.reviewsRatingStar, 10) || 0;
      star.setAttribute("aria-pressed", (value <= this.formRating).toString());
      this.setStars(star, value <= this.formRating ? 5 : 0);
    }
  }

  onPhotosChange() {
    const {
      max,
      maxBytes,
      maxTotalBytes,
      types,
      msgTooMany,
      msgType,
      msgSize,
      msgTotal,
      msgNotAdded,
    } = this.photosRoot.dataset;
    const allowed = (types ?? "").split(",");
    const maxPhotos = parseInt(max, 10) || 5;
    const maxSize = parseInt(maxBytes, 10) || Infinity;
    const maxTotal = parseInt(maxTotalBytes, 10) || Infinity;

    const selected = [...this.photosInput.files];
    // Reset so re-picking the same file still fires a change event.
    this.photosInput.value = "";

    // Judge every pick fresh. The error was otherwise cleared only by a submit, so a
    // rejection stayed on screen after the shopper had already corrected it — which read
    // as "the upload failed" on a review whose photos uploaded perfectly well.
    this.clearFormError();

    // Collect every reason instead of showing them one at a time: showFormError overwrites
    // its own text, so a batch that failed for two different reasons only ever reported the
    // last one, and never said which file was at fault.
    const rejected = [];
    let totalBytes = this.photos.reduce((sum, photo) => sum + photo.size, 0);

    for (const [index, file] of selected.entries()) {
      if (this.photos.length >= maxPhotos) {
        // Name what was dropped. This used to `break` silently, so choosing 8 files kept 5
        // and discarded 3 with nothing on screen accounting for them.
        const dropped = selected.slice(index).map((photo) => photo.name);
        rejected.push(msgTooMany, (msgNotAdded ?? "").replace("__FILES__", dropped.join(", ")));
        break;
      }
      if (!allowed.includes(file.type)) {
        rejected.push(`${file.name}: ${msgType}`);
        continue;
      }
      if (file.size > maxSize) {
        rejected.push(`${file.name}: ${msgSize}`);
        continue;
      }
      if (totalBytes + file.size > maxTotal) {
        rejected.push(`${file.name}: ${msgTotal}`);
        continue;
      }
      totalBytes += file.size;
      this.photos.push(file);
    }

    if (rejected.length > 0) this.showFormError(rejected.filter(Boolean).join(" "));

    this.renderPhotos();
  }

  renderPhotos() {
    if (!this.photosList || !this.photoTemplate) return;

    this.releasePhotoPreviews();
    this.photosList.replaceChildren();

    this.photos.forEach((file, index) => {
      const node = this.photoTemplate.content.cloneNode(true);
      const image = node.querySelector("[data-reviews-photo-image]");
      const previewUrl = URL.createObjectURL(file);
      this.photoPreviewUrls.push(previewUrl);

      if (image) {
        image.src = previewUrl;
        image.alt = file.name;
      }

      node
        .querySelector("[data-reviews-photo-remove]")
        ?.addEventListener("click", () => {
          this.photos.splice(index, 1);
          this.renderPhotos();
        });

      this.photosList.appendChild(node);
    });
  }

  releasePhotoPreviews() {
    for (const url of this.photoPreviewUrls ?? []) URL.revokeObjectURL(url);
    this.photoPreviewUrls = [];
  }

  // Presign + PUT happen at submit time, not at selection: the upload URL is only
  // valid for ~10 minutes, which a shopper can easily outlast while writing, and it
  // avoids leaving orphaned blobs behind for reviews that are never submitted.
  async uploadPhotos() {
    const urls = [];

    for (const file of this.photos) {
      const presigned = await this.request("/reviews/presigned-url", {
        method: "POST",
        body: JSON.stringify({
          fileName: file.name,
          contentType: file.type,
        }),
      });

      // Straight to Azure Blob — not through this.request, since the SAS signature
      // covers Content-Type and the returned headers must be sent verbatim.
      const response = await fetch(presigned.uploadUrl, {
        method: presigned.method ?? "PUT",
        headers: presigned.headers,
        body: file,
      });

      if (!response.ok) throw new Error(`Upload failed (${response.status})`);
      urls.push(presigned.fileUrl);
    }

    return urls;
  }

  setFormStatus(message) {
    if (!this.formStatus) return;
    this.formStatus.textContent = message ?? "";
    this.formStatus.toggleAttribute("hidden", !message);
  }

  async submitReview(event) {
    event.preventDefault();
    if (this.isSubmitting) return;

    const title = this.formTitle?.value.trim() ?? "";
    const body = this.formBody?.value.trim() ?? "";

    if (this.formRating === 0 || title === "" || body === "") {
      this.showFormError(this.formError?.dataset.incomplete);
      return;
    }

    // Judge.me has no idempotency key, so a submission is never retried.
    this.isSubmitting = true;
    this.formSubmit?.setAttribute("disabled", "");
    this.clearFormError();

    let pictureUrls = [];

    if (this.photos.length > 0) {
      this.setFormStatus(this.photosRoot?.dataset.msgUploading);
      try {
        pictureUrls = await this.uploadPhotos();
      } catch (error) {
        // Never submit a review whose photos are missing — let them retry instead.
        console.error("[ProductReviews] photo upload failed", error);
        this.setFormStatus(null);
        // A rejected presign (e.g. INVALID_FILE_TYPE) explains itself better than we can.
        this.showFormError(
          this.apiMessage(error) ?? this.photosRoot?.dataset.msgFailed,
        );
        this.formSubmit?.removeAttribute("disabled");
        this.isSubmitting = false;
        return;
      }
    }

    try {
      await this.request(`/reviews/${this.productId}`, {
        method: "POST",
        body: JSON.stringify({
          customerId: this.customerId,
          customerEmail: this.customerEmail,
          customerName: this.customerName,
          rating: this.formRating,
          title,
          body,
          ...(pictureUrls.length > 0 ? { pictureUrls } : {}),
        }),
      });

      this.releasePhotoPreviews();
      this.photos = [];
      this.setFormStatus(null);
      this.form?.setAttribute("hidden", "");
      this.formSuccess?.removeAttribute("hidden");
    } catch (error) {
      this.setFormStatus(null);

      if (error.code === "ALREADY_REVIEWED") {
        this.showAlreadyReviewed(error.message);
        return;
      }

      this.showFormError(this.apiMessage(error));
      this.formSubmit?.removeAttribute("disabled");
    } finally {
      this.isSubmitting = false;
    }
  }

  // Only the backend's own copy is shopper-facing. A transport failure ("Failed to
  // fetch") carries no code, and must not be shown — callers fall back to theme copy.
  apiMessage(error) {
    return error?.code ? error.message : null;
  }

  // The eligibility endpoint does not detect an existing review — the backend only
  // rejects at submit — so this is the one place a shopper can find out. The message
  // stays on the form, where they just pressed Submit: navigating them back to the list
  // hides the explanation somewhere they are not looking. Submit stays disabled because
  // no amount of retrying will succeed.
  showAlreadyReviewed(message) {
    this.hasCheckedEligibility = true;
    this.showFormError(message);

    // Prepared for when they do go back, and the CTA is retired either way.
    this.writeButton?.setAttribute("hidden", "");
    this.loginLink?.setAttribute("hidden", "");
    this.showNotice(message ?? this.noticeEl?.dataset.msgAlreadyReviewed);
  }

  showNotice(message) {
    if (!this.noticeEl || !message) return;
    this.noticeEl.textContent = message;
    this.noticeEl.removeAttribute("hidden");
  }

  clearFormError() {
    this.formError?.setAttribute("hidden", "");
  }

  showFormError(message) {
    if (!this.formError) return;
    this.formError.textContent = message || this.formError.dataset.fallback;
    this.formError.removeAttribute("hidden");
    // Long forms (photos especially) can push the message out of view.
    this.formError.scrollIntoView({ block: "nearest" });
  }
}

if (!customElements.get("product-reviews")) {
  customElements.define("product-reviews", ProductReviews);
}
